<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { generalPlaces } from '@consumer/api'
import type { Place } from '@consumer/types/serializers'
import { faX } from '@fortawesome/pro-light-svg-icons'
import { typeaheadDebounce } from '@shared/debounce'

withDefaults(defineProps<{
  initialPlaces: Place[]
  topGeneralPlaces: Place[] | undefined
  isPlaceSelected: ((place: Place) => boolean)
  search?: boolean
}>(), { topGeneralPlaces: () => ([]), })

const emit = defineEmits<{ togglePlace: [place: Place] }>()

let searchQuery = _ref<string>()
let filteredPlaces = _ref<Place[]>()
let searching = _ref(false)
let keyboardIndex = _ref(-1)

function samePlace (place: Place, otherPlace: Place) {
  return place.name?.toLowerCase() === otherPlace.name?.toLowerCase()
}

const places = _computed(
  () => filteredPlaces.value || [
    ...__props.initialPlaces.filter(place => !__props.topGeneralPlaces.some(generalPlace => samePlace(place, generalPlace))),
    ...__props.topGeneralPlaces,
  ],
)

const searchPlaces = typeaheadDebounce(async (value: string) => {
  const searchValue = value.trim().toLowerCase()

  if (searchValue === '') {
    resetSearch()
    return
  }

  searching.value = true

  keyboardIndex.value = 0

  filteredPlaces.value = await generalPlaces.index<Place[]>({ query: { name: value } })

  searching.value = false
})

function resetSearch () {
  searchQuery.value = ''
  filteredPlaces.value = undefined
  keyboardIndex.value = -1
}

watch(() => searchQuery.value, searchPlaces)

watch(
  () => keyboardIndex.value,
  () => {
    // Scroll into view the element
    const element = document.querySelectorAll('[name="nc_general_place"]')[keyboardIndex.value]
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  },
)
</script>

<template>
  <div class="flex flex-col h-fit" :style="{minHeight: search ? '550px' : 'auto'}">
    <h3 v-if="search" class="text-xl w-full text-center mb-2">or search general places</h3>
    <GInput
      v-if="search"
      v-model="searchQuery"
      class="w-full"
      placeholder="e.g 'Any sushi restaurant'"
      @update:modelValue="searchQuery = $event"
      @keydown.down.prevent="keyboardIndex = (keyboardIndex + 1) % places.length"
      @keydown.up.prevent="keyboardIndex = keyboardIndex == 0 ? places.length - 1 : Math.max(keyboardIndex - 1, 0)"
      @keydown.enter.prevent="emit('togglePlace', places[keyboardIndex])"
      @keydown.esc.prevent="resetSearch"
      @mousemove.capture.prevent
    >
      <template #addonRight>
        <div class="group">
          <GIconButton v-show="searchQuery?.length" :icon="faX" @click="resetSearch"/>
        </div>
      </template>
    </GInput>
    <div v-if="searching" class="w-full justify-center flex items-center gap-2 flex-grow">
      <LoadingIndicator class=""/>
      Searching ...
    </div>
    <div
      v-else
      class="mt-2 w-full flex flex-wrap gap-4 text-v2-secondary overflow-y-auto shadow-white inset-shadow-sm p-2"
      @mousemove="keyboardIndex = -1"
    >
      <div
        v-if="!places.length"
        class="flex items-center justify-center w-full flex-grow"
      >
        Nothing found... try a different search term.
      </div>
      <GButton
        v-for="place, i in places"
        :key="i"
        name="nc_general_place"
        class="g-button-tab flex-grow"
        :class="{ active: isPlaceSelected(place), hover: keyboardIndex === i }"
        :data-selected="isPlaceSelected(place) ? 'true' : 'false'"
        style="height: 52px;"
        secondary
        :label="place.name"
        @click="emit('togglePlace', place)"
      />
    </div>
  </div>
</template>
